import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, Check, RefreshCw } from '../../../icons';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import {
  fetchSystemSettings, saveSystemSettings, testSystemMail, type SystemSettingView,
} from '../../../api/aiProviders';

/**
 * 관리 콘솔 › 시스템 설정 › **메일** (판 O · 2026-09-10 사용자 지시).
 *
 * 8443 「메일 › 환경설정 › 외부메일」 화면을 참조했다 — **받기(IMAP)** 와 **보내기(SMTP)** 를
 * 나란히 두고, 주소·포트·보안연결·아이디·비밀번호를 같은 차례로 묻는다. 다른 점은 하나다:
 * 저쪽은 «사람마다 제 계정»이고 여기는 **«서버 한 벌»** 이다.
 *
 * ## ★★★ 지금 무엇이 답하고 있나
 *
 * 표에 줄이 없으면 **서버 파일(env)** 값이 답한다. 그 사실을 화면이 말해야
 * 「분명 콘솔에서 고쳤는데」와 「아직 파일 값이다」를 가릴 수 있다.
 *
 * ## ★★★ 비밀번호는 원문이 «오지 않는다»
 *
 * 담겨 있나(`hasValue`)만 온다. 비워 두면 **「그대로 둔다」**이고, 지우려면 「지우기」를 켠다.
 * 이 규칙이 없으면 **주소 한 글자를 고칠 때마다 비밀번호가 지워진다**(AI 연결에서 겪은 자리다).
 *
 * ## ★ 받기(IMAP)는 «사내 서버 기본값»이다
 *
 * 시스템이 IMAP 으로 메일을 가져오지는 않는다. 이 값은 **사원이 외부메일을 붙일 때**
 * 주소·포트를 미리 채워 주는 데 쓰인다(사내 도메인일 때만). 화면이 그렇게 적는다 —
 * 안 적으면 「설정했는데 아무 일도 안 일어난다」가 된다.
 */

type Draft = Record<string, string>;

const SMTP_FIELDS = [
  { key: 'mail.smtp.host', label: '보내기 서버(SMTP)', ph: 'mail.example.co.kr' },
  { key: 'mail.smtp.port', label: '포트', ph: '587' },
  { key: 'mail.smtp.user', label: '아이디', ph: 'no-reply@example.co.kr' },
  { key: 'mail.smtp.from', label: '보내는 주소', ph: 'no-reply@example.co.kr' },
] as const;

const IMAP_FIELDS = [
  { key: 'mail.domain', label: '사내 메일 도메인', ph: 'example.co.kr' },
  { key: 'mail.imap.host', label: '받기 서버(IMAP)', ph: 'imap.example.co.kr' },
  { key: 'mail.imap.port', label: '포트', ph: '993' },
] as const;

/**
 * 메일로 보낼 수 있는 알림 종류 — `notification-type-meta.ts` 의 앞머리와 같은 어휘.
 *
 * ★★★ 이 목록이 «가리는 자리»다. 알림 파이프라인은 종류를 안 가려서, 이것 없이 SMTP 를 켜면
 *   채팅 한 통마다 메일 한 통이 나간다. 그래서 기본은 「AI 토큰 한도」 하나뿐이다.
 */
const NOTIFY_KINDS = [
  { key: 'ai.quota', label: 'AI 토큰 한도', desc: '한도의 80% · 100% 를 넘었을 때 (개인 · 회사)' },
  { key: 'approval', label: '전자결재', desc: '결재 요청 · 승인 · 반려' },
  { key: 'calendar', label: '일정', desc: '일정 리마인더' },
  { key: 'board_comment', label: '게시글 댓글', desc: '내 글에 달린 댓글' },
  { key: 'board.', label: '게시판', desc: '게시판 알림' },
  { key: 'mail', label: '메일 도착', desc: '새 메일' },
  { key: 'chat_message', label: '채팅 메시지', desc: '메시지 한 통마다 메일 한 통이 나갑니다', warn: true },
] as const;

/** 저장 형태는 쉼표 목록 — 서버의 `parseNotifyTypes` 와 같은 규칙. */
function splitKinds(raw: string): string[] {
  return [...new Set(raw.split(/[,\n]/).map((x) => x.trim()).filter((x) => x !== ''))];
}

export function SystemMailSection(): JSX.Element {
  const [rows, setRows] = useState<SystemSettingView[] | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [pass, setPass] = useState('');
  const [clearPass, setClearPass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState<{ ok: boolean; message: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await fetchSystemSettings();
      setRows(s);
      setDraft(Object.fromEntries(s.filter((r) => !r.secret).map((r) => [r.key, r.value ?? ''])));
      setPass('');
      setClearPass(false);
    } catch {
      setRows(null);
      setError('설정을 불러오지 못했습니다.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const byKey = useMemo(
    () => new Map((rows ?? []).map((r) => [r.key, r])),
    [rows],
  );
  const passRow = byKey.get('mail.smtp.pass');
  /** 아직 서버 파일이 답하는 값이 있나 — 있으면 화면이 그렇게 말한다. */
  const fromEnv = (rows ?? []).filter((r) => !r.fromTable
    && ((r.secret ? r.hasValue : (r.value ?? '') !== '')));

  const set = (k: string, v: string): void => setDraft((p) => ({ ...p, [k]: v }));
  const secure = (k: string): boolean => draft[k] === 'true';

  /* ── 어떤 알림을 메일로 ─────────────────────────────────────────────────── */
  const notifyKinds = splitKinds(draft['mail.notify.types'] ?? '');
  const toggleKind = (k: string, on: boolean): void => set(
    'mail.notify.types',
    (on ? [...notifyKinds, k] : notifyKinds.filter((x) => x !== k)).join(','),
  );
  /* ★ 목록에 없는 앞머리를 누가 API 로 적어 뒀을 수 있다 — 화면이 조용히 지우지 않는다. */
  const extraKinds = notifyKinds.filter((k) => !NOTIFY_KINDS.some((n) => n.key === k));

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const payload: Record<string, string> = { ...draft };
      /* ★ 비밀번호는 «적었거나 지우기로 했을 때»만 넣는다 — 안 넣으면 서버가 예전 것을 지킨다. */
      if (pass.trim() !== '') payload['mail.smtp.pass'] = pass.trim();
      else if (clearPass) payload['mail.smtp.pass'] = '';
      const s = await saveSystemSettings(payload);
      setRows(s);
      setPass('');
      setClearPass(false);
      setNote('저장했습니다. 이제 관리 콘솔 값이 서버 파일보다 먼저 쓰입니다.');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const runTest = async (): Promise<void> => {
    setBusy(true);
    setProbe(null);
    try {
      setProbe(await testSystemMail({
        host: draft['mail.smtp.host'] ?? '',
        port: draft['mail.smtp.port'] ?? '',
        secure: draft['mail.smtp.secure'] ?? '',
        user: draft['mail.smtp.user'] ?? '',
        /* 비밀번호를 안 적었으면 안 보낸다 — 서버가 담아 둔 것으로 시험한다. */
        ...(pass.trim() !== '' ? { pass: pass.trim() } : {}),
      }));
    } catch {
      setProbe({ ok: false, message: '시험하지 못했습니다.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConsoleListScreen
      title="메일"
      kpi={rows === null ? '—' : (byKey.get('mail.smtp.host')?.value ? '설정됨' : '설정 안 됨')}
      desc="서버가 보내는 메일(알림·안내)과, 사원이 외부메일을 붙일 때 채워 줄 사내 서버 기본값입니다. 8443 「메일 › 환경설정 › 외부메일」과 같은 차례로 묻습니다 — 다른 점은 저쪽이 «사람마다»이고 여기는 «서버 한 벌»이라는 것입니다."
      toolsExtra={(
        <button type="button" className="svc-btn" disabled={busy} onClick={() => { void load(); }}>
          <RefreshCw size={14} aria-hidden="true" /> 다시 읽기
        </button>
      )}
      notice={(
        <>
          {error !== null && (
            <div className="admin-console__alert" role="alert">
              <AlertTriangle size={16} aria-hidden="true" /><span>{error}</span>
            </div>
          )}
          {note !== null && (
            <div className="admin-console__alert" role="status">
              <Check size={16} aria-hidden="true" /><span>{note}</span>
            </div>
          )}
          {/* ★★★ 「지금 무엇이 답하고 있나」 — 안 적으면 고쳐 놓고도 왜 그대로인지 모른다. */}
          {fromEnv.length > 0 && (
            <div className="admin-console__alert" role="status">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                아직 <b>서버 파일(.env)</b> 값이 답하고 있는 항목이 {fromEnv.length}개 있습니다 —
                여기서 저장하면 그때부터 이 값이 먼저 쓰입니다.
              </span>
            </div>
          )}
        </>
      )}
    >
      {rows === null ? (
        <p className="cl-empty">{error === null ? '읽는 중…' : '보여 줄 것이 없습니다.'}</p>
      ) : (
        <div className="sysmail">
          {/* ── 보내기(SMTP) — 실제로 시스템이 쓰는 값 ───────────────────── */}
          <section className="sysmail__sec">
            <h3 className="sysmail__h">보내기 (SMTP)</h3>
            <p className="sysmail__hint">
              알림·안내 메일을 이 서버로 보냅니다. 비어 있으면 <b>메일이 한 통도 안 나갑니다</b>.
            </p>
            {SMTP_FIELDS.map((f) => (
              <label key={f.key} className="cl-field">
                <span>{f.label}</span>
                <input
                  value={draft[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.ph}
                  aria-label={f.label}
                />
              </label>
            ))}

            <label className="cl-field">
              <span>비밀번호</span>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder={passRow?.hasValue ? '저장돼 있음 — 바꿀 때만 입력' : '비밀번호'}
                autoComplete="new-password"
                aria-label="비밀번호"
              />
            </label>
            {/* ★ 비워 두면 「그대로 둔다」 — 지우려면 명시해야 한다. */}
            {passRow?.hasValue === true && (
              <label className="cl-check">
                <input type="checkbox" checked={clearPass} onChange={(e) => setClearPass(e.target.checked)} />
                <span>저장된 비밀번호 지우기</span>
              </label>
            )}

            <label className="cl-check">
              <input
                type="checkbox"
                checked={secure('mail.smtp.secure')}
                onChange={(e) => set('mail.smtp.secure', e.target.checked ? 'true' : 'false')}
              />
              <span>보안연결(SSL) — 465 포트면 켭니다. 587 은 보통 끕니다(STARTTLS).</span>
            </label>

            <p className="sysmail__hint">
              <button type="button" className="svc-btn" disabled={busy} onClick={() => { void runTest(); }}>
                연결 시험
              </button>
              {/* ★ 시험은 저장하지 않는다 — 틀린 값으로 저장부터 하면 그 사이 메일이 다 실패한다. */}
              {' '}저장하지 않고 붙어만 봅니다.
              {probe !== null && (
                <span className={probe.ok ? 'sysmail__ok' : 'sysmail__bad'}> — {probe.message}</span>
              )}
            </p>
          </section>

          {/* ── 어떤 알림을 메일로 (판 P) ────────────────────────────────── */}
          <section className="sysmail__sec">
            <h3 className="sysmail__h">
              <Bell size={15} aria-hidden="true" /> 어떤 알림을 메일로 보낼까
            </h3>
            <p className="sysmail__hint">
              ★ 고른 종류만 메일로 나갑니다. <b>하나도 안 고르면 한 통도 안 나갑니다.</b>
              {' '}벨·모바일 푸시는 이 설정과 <b>무관</b>하게 종전대로 갑니다.
            </p>
            <p className="sysmail__hint">
              받는 사람은 <b>본인이 켠 사람뿐</b>입니다 — 사원이 8443 「통합설정 › 알림 › 외부 알림
              연동」에서 <b>외부 이메일</b>에 주소를 넣어야 그 사람에게 갑니다. 아무도 안 넣었으면
              여기서 켜도 메일은 안 나갑니다.
            </p>
            {NOTIFY_KINDS.map((n) => (
              <label key={n.key} className="cl-check">
                <input
                  type="checkbox"
                  checked={notifyKinds.includes(n.key)}
                  onChange={(e) => toggleKind(n.key, e.target.checked)}
                  aria-label={n.label}
                />
                <span>
                  <b>{n.label}</b> — {n.desc}
                  {'warn' in n && n.warn === true && (
                    <>
                      {' '}
                      <AlertTriangle size={13} aria-hidden="true" />
                      <b> 양이 많습니다.</b>
                    </>
                  )}
                </span>
              </label>
            ))}
            {extraKinds.length > 0 && (
              <p className="sysmail__hint">
                목록에 없는 종류도 적혀 있습니다: <b>{extraKinds.join(', ')}</b> — 그대로 둡니다.
              </p>
            )}
          </section>

          {/* ── 받기(IMAP) — 사내 서버 «기본값» ──────────────────────────── */}
          <section className="sysmail__sec">
            <h3 className="sysmail__h">받기 (IMAP) — 사내 서버 기본값</h3>
            <p className="sysmail__hint">
              ★ 시스템이 이 서버에서 메일을 가져오지는 <b>않습니다</b>. 사원이 8443 「메일 › 환경설정 ›
              외부메일」에서 <b>사내 주소</b>로 계정을 붙일 때 주소·포트를 <b>미리 채워 주는</b> 값입니다.
              도메인이 비어 있으면 아무 데도 안 쓰입니다.
            </p>
            {IMAP_FIELDS.map((f) => (
              <label key={f.key} className="cl-field">
                <span>{f.label}</span>
                <input
                  value={draft[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.ph}
                  aria-label={f.label}
                />
              </label>
            ))}
            <label className="cl-check">
              <input
                type="checkbox"
                checked={draft['mail.imap.secure'] !== 'false'}
                onChange={(e) => set('mail.imap.secure', e.target.checked ? 'true' : 'false')}
              />
              <span>보안연결(SSL) — IMAP 993 이면 켭니다.</span>
            </label>
          </section>

          <div className="sysmail__foot">
            <button type="button" className="svc-btn svc-btn--primary" disabled={busy} onClick={() => { void save(); }}>
              <Check size={14} aria-hidden="true" /> {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      )}
    </ConsoleListScreen>
  );
}
